import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useUploadStore } from '@/stores/useUploadStore';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

interface MappingStepProps {
  onNext: () => void;
  onBack: () => void;
}

const REQUIRED_FIELDS = [
  { value: 'id_number', label: 'ID Number', required: true },
  { value: 'principal', label: 'Principal', required: true },
  { value: 'balance', label: 'Balance', required: true },
  { value: 'start_date', label: 'Start Date', required: false },
  { value: 'last_payment_date', label: 'Last Payment Date', required: false },
  { value: 'instalment', label: 'Instalment', required: false },
  { value: 'status', label: 'Status', required: false },
];

export function MappingStep({ onNext, onBack }: MappingStepProps) {
  const { headers, sample, suggestions, mapping, setMapping } = useUploadStore();

  // Initialize mapping with suggestions
  useEffect(() => {
    if (Object.keys(mapping).length === 0 && Object.keys(suggestions).length > 0) {
      setMapping(suggestions);
    }
  }, [suggestions, mapping, setMapping]);

  const handleMappingChange = (sourceCol: string, targetCol: string) => {
    setMapping({ ...mapping, [sourceCol]: targetCol });
  };

  const handleNext = () => {
    // Validate required fields are mapped
    const requiredFields = REQUIRED_FIELDS.filter(f => f.required).map(f => f.value);
    const mappedTargets = Object.values(mapping);
    const missingRequired = requiredFields.filter(f => !mappedTargets.includes(f));

    if (missingRequired.length > 0) {
      toast.error(`Please map required fields: ${missingRequired.join(', ')}`);
      return;
    }

    onNext();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Map Columns</h2>
        <p className="text-muted-foreground">
          Map your file columns to portfolio fields
        </p>
      </div>

      <div className="grid gap-4">
        {REQUIRED_FIELDS.map((field) => {
          const sourceCol = Object.keys(mapping).find(k => mapping[k] === field.value);
          
          return (
            <Card key={field.value}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{field.label}</CardTitle>
                  {field.required && <Badge variant="destructive">Required</Badge>}
                </div>
                <CardDescription>
                  Select the source column from your file
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label>Source Column</Label>
                  <Select
                    value={sourceCol || ''}
                    onValueChange={(value) => handleMappingChange(value, field.value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a column..." />
                    </SelectTrigger>
                    <SelectContent>
                      {headers.map((header) => (
                        <SelectItem key={header} value={header}>
                          {header}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {sourceCol && sample[0] && (
                    <div className="mt-2 p-2 rounded bg-muted text-sm">
                      <span className="text-muted-foreground">Sample: </span>
                      <span className="font-mono">{sample[0][sourceCol]}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="flex gap-2">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button onClick={handleNext} className="ml-auto">
          Continue
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
